import { useInView } from "framer-motion";
import { useId, useRef } from "react";
import CountUp from "react-countup";
import { TriangleIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export type ReportsViewMetricItem = {
  label?: string;
  value: number;
  delta?: number | null;
};

// A figure is set to the column it stands in, not to the window: two of them
// side by side in a widget half a page wide are each a quarter of it, and a
// size read off the viewport had them running over the card and each other.
// ``cqi`` is that column, which the figure's own box declares below.
function ReportsViewMetricValue({
  value,
  isLoading,
  delta = null,
  compact = false,
}: {
  value: number;
  isLoading: boolean;
  delta?: number | null;
  compact?: boolean;
}) {
  const id = useId();
  const isIncreasing = delta != null && delta > 0;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div
      ref={ref}
      // What the figure inside is measured against.
      className="flex flex-col items-center w-full @container"
    >
      {!isLoading && (
        <CountUp
          start={0}
          end={isInView ? value : 0}
          duration={2.5}
          prefix="$"
          separator=","
        >
          {({ countUpRef }) => (
            <Typography
              ref={countUpRef}
              id={id}
              component="span"
              className={cn(
                compact
                  ? "font-mono font-medium [font-weight:500] text-foreground text-[clamp(1.25rem,16cqi,2.25rem)] [line-height:1.05] [letter-spacing:-0.03em] text-center"
                  : "font-mono font-medium [font-weight:500] text-foreground text-[clamp(1.5rem,20cqi,3.75rem)] [line-height:1.05] [letter-spacing:-0.03em] text-center",
              )}
            />
          )}
        </CountUp>
      )}
      {isLoading && (
        <Skeleton
          variant="rectangular"
          className={cn(
            compact ? "w-[80%]" : "w-[70%]",
            compact ? "max-w-40" : "max-w-70",
            compact ? "h-9 md:h-11" : "h-12 md:h-15",
            "rounded-sm",
          )}
        />
      )}
      {delta != null && (
        <Typography
          level="mono-h3"
          // Its own colour, faint, behind the colour itself: up is good
          // news, down is not.
          className={cn(
            "flex items-center gap-1.5",
            "pl-2 pr-2 pt-1 pb-1 rounded",
            isIncreasing
              ? "bg-[color-mix(in oklab, var(--muted-foreground) 12.5%, transparent)] text-[var(--muted-foreground)]"
              : "bg-[color-mix(in oklab, var(--destructive) 12.5%, transparent)] text-[var(--destructive)]",
          )}
        >
          <TriangleIcon
            // Filled: a mark, not a drawing.
            fill="currentColor"
            size={8}
            style={{
              transform:
                isIncreasing === null
                  ? "rotate(90deg)"
                  : isIncreasing
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
              transition: "all 0.2s",
            }}
          />
          <Skeleton loading={isLoading}>{delta}%</Skeleton>
        </Typography>
      )}
    </div>
  );
}

export function ReportsViewMetricWidget({
  items,
  value,
  isLoading = false,
  delta = null,
}: {
  items?: ReportsViewMetricItem[];
  value?: number;
  isLoading?: boolean;
  delta?: number | null;
}) {
  const metrics = items ?? (value != null ? [{ value, delta }] : []);
  const isMulti = metrics.length > 1;

  if (!isMulti) {
    const metric = metrics[0];
    return (
      <ReportsViewMetricValue
        value={metric?.value ?? 0}
        isLoading={isLoading}
        delta={metric?.delta ?? delta}
      />
    );
  }

  return (
    <div
      // Figures share a row while there is room for them, and go under one
      // another when there is not - a strip of three held in one line on a
      // narrow card had each of them spilling over its neighbour.
      className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-4 w-full max-w-180"
    >
      {metrics.map((metric, index) => (
        <div
          key={metric.label ?? index}
          className="flex flex-col items-center gap-1.5 min-w-0"
        >
          {metric.label && (
            <Typography
              level="body-xs"
              className="text-muted-foreground/80 uppercase [letter-spacing:0.05em] text-center"
            >
              {metric.label}
            </Typography>
          )}
          <ReportsViewMetricValue
            value={metric.value}
            isLoading={isLoading}
            delta={metric.delta}
            compact
          />
        </div>
      ))}
    </div>
  );
}
